import { useLayoutEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Banner } from '@immo/ui-kit';
import { useTranslations } from '@/i18n/use-translations';
import { formatLogLine } from '@/lib/landiq/format-log-line';
import type { LogLine } from '@/lib/landiq/run-poll';

const STICK_TO_BOTTOM_PX = 8;

type Props = {
  readonly renderJobId: string | null;
  readonly lines: ReadonlyArray<LogLine>;
};

export function RunLog({ renderJobId, lines }: Props) {
  const t = useTranslations('pages.admin.landiq-data-run');
  const intl = useIntl();
  const box = useRef<HTMLDivElement>(null);
  const wasAtBottom = useRef(true);

  useLayoutEffect(() => {
    const element = box.current;

    if (element === null) return;

    if (wasAtBottom.current) element.scrollTop = element.scrollHeight;
  }, [lines]);

  if (renderJobId === null) {
    return <Banner variant="info" title={t('no-log')} />;
  }

  const rememberPosition = () => {
    const element = box.current;

    if (element === null) return;

    wasAtBottom.current = element.scrollHeight - element.scrollTop - element.clientHeight <= STICK_TO_BOTTOM_PX;
  };

  return (
    <div
      ref={box}
      role="log"
      onScroll={rememberPosition}
      className="max-h-[320px] overflow-y-auto rounded-md border border-border bg-surface px-3 py-2 font-mono text-sm"
    >
      {lines.map((line, index) => {
        const { message, rest } = formatLogLine(line.text);

        return (
          <div key={`${index}:${line.at}`} className="truncate">
            <span className="text-text-tertiary">
              {intl.formatTime(line.at, {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                timeZone: 'Europe/Berlin',
              })}
            </span>
            {'  '}
            {message}
            {rest !== null && <span className="text-text-tertiary">{`  ${rest}`}</span>}
          </div>
        );
      })}
    </div>
  );
}
